import React, { CSSProperties, useEffect, useMemo, useRef, useState } from "react"
import { Card, Empty, Input, Table, Tooltip, Typography, message } from "antd"
import { AppstoreOutlined, TableOutlined } from "@ant-design/icons"
import { ISapAssetDetail } from "@/types/sapAsset.type"
import { getAdaptiveColumnWidths, shouldShowCellTooltip } from "../../shared/utils/editTableSizing"
import { getAssetColumns } from "../utils/assetColumns"
import { getAssetNumberingGroupInfo } from "../utils/assetNumbering"

const { Text } = Typography

interface AssetColumn {
  key: string
  label: string
  required?: boolean
}

interface Props {
  items: ISapAssetDetail[]
  disabled?: boolean
  templateKey?: string | null
  numberingMode?: boolean
  onChange: (items: ISapAssetDetail[]) => void
}

const PASTE_HIGHLIGHT_DURATION_MS = 2500

const AssetPayloadEditor: React.FC<Props> = ({ items, disabled = false, templateKey = null, numberingMode = false, onChange }) => {
  const [isMobile, setIsMobile] = useState(false)
  const [highlightedCells, setHighlightedCells] = useState<string[]>([])
  const highlightTimeoutRef = useRef<number | null>(null)

  const editableColumns = useMemo(() => getAssetColumns(templateKey, items) as AssetColumn[], [templateKey, items])

  const groupStats = useMemo(() => {
    const stats = new Map<string, { count: number; firstIndex: number }>()
    items.forEach((item, index) => {
      const groupKey = String(item.numbering_group_key || item._id || `${item.record_id || "asset"}-group-${index + 1}`)
      const current = stats.get(groupKey) || { count: 0, firstIndex: index }
      current.count += 1
      stats.set(groupKey, current)
    })
    return stats
  }, [items])

  const buildHeaderCellStyle = (width?: number): CSSProperties => ({
    whiteSpace: "nowrap",
    wordBreak: "keep-all",
    minWidth: width,
    maxWidth: width,
  })

  const readCellValue = (item: ISapAssetDetail, key: string): string => {
    const rawValue = item[key]
    if (rawValue === null || rawValue === undefined) return ""
    return String(rawValue)
  }

  const columnWidths = useMemo(() => getAdaptiveColumnWidths(editableColumns, items), [editableColumns, items])

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768)
    checkMobile()
    window.addEventListener("resize", checkMobile)
    return () => window.removeEventListener("resize", checkMobile)
  }, [])

  useEffect(
    () => () => {
      if (highlightTimeoutRef.current) {
        window.clearTimeout(highlightTimeoutRef.current)
      }
    },
    []
  )

  const isStructuredPasteField = (key: string) => numberingMode && (key === "asset_number" || key === "asset_subnumber")

  const handleCellChange = (rowIndex: number, key: string, value: string) => {
    if (disabled) return

    const nextItems = items.map((item, index) => {
      if (index !== rowIndex) return item
      return {
        ...item,
        [key]: value,
      }
    })
    onChange(nextItems)
  }

  const handleStructuredPaste = (event: React.ClipboardEvent<HTMLInputElement>, rowIndex: number, key: string) => {
    if (disabled || !isStructuredPasteField(key)) return

    const rawClipboard = event.clipboardData.getData("text/plain")
    if (!rawClipboard.includes("\n") && !rawClipboard.includes("\t")) {
      return
    }

    event.preventDefault()

    const sourceRow = items[rowIndex]
    const sourceGroupKey = String(sourceRow?.numbering_group_key || sourceRow?._id || `${sourceRow?.record_id || "asset"}-group-${rowIndex + 1}`)
    const targetIndexes = items
      .map((item, index) => ({ item, index }))
      .filter(({ item, index }) => String(item.numbering_group_key || item._id || `${item.record_id || "asset"}-group-${index + 1}`) === sourceGroupKey && index >= rowIndex)
      .map(({ index }) => index)

    const rows = rawClipboard
      .replace(/\r/g, "")
      .split("\n")
      .filter((line, index, source) => !(index === source.length - 1 && line === ""))
      .map(line => line.split("\t"))

    if (!rows.length) {
      return
    }

    const maxColumnCount = Math.max(...rows.map(row => row.length))
    if (key === "asset_subnumber" && maxColumnCount > 1) {
      void message.warning("Paste into Asset Subnumber accepts one Excel column only.")
      return
    }

    if (key === "asset_number" && maxColumnCount > 2) {
      void message.warning("Paste into Asset Number accepts one or two Excel columns only.")
      return
    }

    if (rows.length > 1 && rows.length !== targetIndexes.length) {
      void message.warning("The pasted Excel row count must match the remaining rows in the selected item block.")
      return
    }

    if (rows.length === 1 && targetIndexes.length === 0) {
      return
    }

    const appliedIndexes = rows.length > 1 ? targetIndexes : [rowIndex]
    if (rows.length > appliedIndexes.length) {
      void message.warning("The pasted Excel rows exceed the available rows in the selected item block.")
      return
    }

    const highlightKeys: string[] = []
    const nextItems = items.map((item, currentIndex) => {
      const targetPosition = appliedIndexes.indexOf(currentIndex)
      if (targetPosition === -1) {
        return item
      }

      const rowValues = rows[Math.min(targetPosition, rows.length - 1)] || []
      const nextItem = { ...item }

      if (key === "asset_number") {
        nextItem.asset_number = (rowValues[0] || "").trim()
        highlightKeys.push(`${currentIndex}:asset_number`)
        if (maxColumnCount === 2) {
          nextItem.asset_subnumber = (rowValues[1] || "").trim()
          highlightKeys.push(`${currentIndex}:asset_subnumber`)
        }
      } else {
        nextItem.asset_subnumber = (rowValues[0] || "").trim()
        highlightKeys.push(`${currentIndex}:asset_subnumber`)
      }

      return nextItem
    })

    if (highlightTimeoutRef.current) {
      window.clearTimeout(highlightTimeoutRef.current)
    }

    setHighlightedCells(highlightKeys)
    highlightTimeoutRef.current = window.setTimeout(() => {
      setHighlightedCells([])
      highlightTimeoutRef.current = null
    }, PASTE_HIGHLIGHT_DURATION_MS)

    onChange(nextItems)
    void message.success(`Pasted Excel values into ${rows.length > 1 ? rows.length : 1} row${rows.length > 1 ? "s" : ""} in the selected item block.`)
  }

  const getInputStyle = (rowIndex: number, key: string): CSSProperties | undefined => {
    const isHighlighted = highlightedCells.includes(`${rowIndex}:${key}`)
    if (!isHighlighted) return undefined

    return {
      backgroundColor: "#fff7cc",
      borderColor: "#f7c948",
    }
  }

  if (!items.length) {
    return (
      <Card
        title={
          <>
            <TableOutlined /> Edit Asset Data
          </>
        }
        style={{ borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}
      >
        <Empty description="No data to edit" />
      </Card>
    )
  }

  return (
    <Card title={<>{isMobile ? <AppstoreOutlined /> : <TableOutlined />} Edit Asset Data</>} style={{ borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }} bodyStyle={{ padding: 0 }}>
      {isMobile ? (
        <div style={{ padding: 16, background: "#fafafa", maxHeight: 560, overflowY: "auto" }}>
          {items.map((item, rowIndex) => {
            const groupKey = String(item.numbering_group_key || item._id || `${item.record_id || "asset"}-group-${rowIndex + 1}`)
            const groupData = groupStats.get(groupKey)
            const isFirstRowInGroup = groupData?.firstIndex === rowIndex
            const groupInfo = numberingMode ? getAssetNumberingGroupInfo(item, groupData?.count || 1) : null
            const rowLabel = numberingMode ? `Row ${Number(item.numbering_row_order) || rowIndex + 1}` : `Row ${rowIndex + 1}`

            return (
              <Card
                key={item._id || `${item.record_id || "asset"}-${rowIndex}`}
                size="small"
                style={{
                  marginBottom: 16,
                  marginTop: numberingMode && isFirstRowInGroup && rowIndex > 0 ? 8 : 0,
                  borderRadius: 8,
                  border: numberingMode && isFirstRowInGroup ? "1px solid #ffd591" : "1px solid #f0f0f0",
                }}
                title={
                  numberingMode ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      {isFirstRowInGroup && groupInfo && (
                        <>
                          <Text strong style={{ fontSize: 13 }}>
                            {groupInfo.title}
                          </Text>
                          <Text style={{ fontSize: 11, color: "#8c8c8c" }}>{groupInfo.helper}</Text>
                        </>
                      )}
                      <Text style={{ fontSize: 12, color: "#595959" }}>{rowLabel}</Text>
                    </div>
                  ) : (
                    rowLabel
                  )
                }
              >
                {editableColumns.map((column, columnIndex) => {
                  const value = readCellValue(item, column.key)

                  return (
                    <div
                      key={column.key}
                      style={{
                        marginBottom: columnIndex === editableColumns.length - 1 ? 0 : 12,
                        borderBottom: columnIndex === editableColumns.length - 1 ? "none" : "1px dashed #f0f0f0",
                        paddingBottom: columnIndex === editableColumns.length - 1 ? 0 : 12,
                      }}
                    >
                      <Text style={{ display: "block", fontSize: 12, color: "#8c8c8c", marginBottom: 6 }}>
                        {column.label}
                        {column.required && <span style={{ color: "#ff4d4f" }}> *</span>}
                      </Text>
                      <Input
                        value={value}
                        disabled={disabled}
                        onChange={event => handleCellChange(rowIndex, column.key, event.target.value)}
                        onPaste={event => handleStructuredPaste(event, rowIndex, column.key)}
                        style={getInputStyle(rowIndex, column.key)}
                      />
                    </div>
                  )
                })}
              </Card>
            )
          })}
        </div>
      ) : (
        <Table
          className="asset-payload-editor-table"
          rowKey={(row, index) => row._id || `${row.record_id || "asset"}-${index?.toString() || "0"}`}
          dataSource={items}
          pagination={numberingMode ? false : { pageSize: 10 }}
          size="middle"
          scroll={{ x: "max-content", y: 620 }}
          tableLayout="auto"
          columns={[
            {
              title: "Row",
              key: "_rowNumber",
              width: 70,
              onHeaderCell: () => ({
                style: buildHeaderCellStyle(70),
              }),
              render: (_value: unknown, _row: ISapAssetDetail, rowIndex?: number) => (rowIndex || 0) + 1,
            },
            ...(numberingMode
              ? [
                  {
                    title: "Group",
                    key: "_itemBlock",
                    width: 150,
                    onHeaderCell: () => ({
                      style: buildHeaderCellStyle(150),
                    }),
                    render: (_value: unknown, row: ISapAssetDetail, rowIndex?: number) => {
                      const groupKey = String(row.numbering_group_key || row._id || row.record_id || "asset-group")
                      const groupData = groupStats.get(groupKey)
                      const isFirstRowInGroup = groupData?.firstIndex === (rowIndex || 0)
                      if (!isFirstRowInGroup) {
                        return <Text style={{ fontSize: 12, color: "#bfbfbf" }}>�</Text>
                      }

                      const groupInfo = getAssetNumberingGroupInfo(row, groupData?.count || 1)
                      return (
                        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                          <Text strong style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                            {groupInfo.title}
                          </Text>
                          <Text style={{ fontSize: 11, color: "#8c8c8c", whiteSpace: "nowrap" }}>{groupInfo.helper}</Text>
                        </div>
                      )
                    },
                  },
                ]
              : []),
            ...editableColumns.map(column => {
              const columnWidth = columnWidths[column.key] || 180
              const label = `${column.label}${column.required ? " *" : ""}`

              return {
                title: (
                  <Tooltip title={label}>
                    <div
                      style={{
                        display: "block",
                        color: "#ffffff",
                        fontWeight: 600,
                        lineHeight: 1.2,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        minWidth: columnWidth - 24,
                      }}
                    >
                      {label}
                    </div>
                  </Tooltip>
                ),
                dataIndex: column.key,
                key: column.key,
                width: columnWidth,
                onHeaderCell: () => ({
                  style: buildHeaderCellStyle(columnWidth),
                }),
                render: (_value: unknown, _: ISapAssetDetail, rowIndex?: number) => {
                  const currentValue = readCellValue(items[rowIndex || 0] || {}, column.key)
                  const inputNode = (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      <Text
                        style={{
                          display: "block",
                          fontSize: 11,
                          color: "#8c8c8c",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {label}
                      </Text>
                      <Input
                        value={currentValue}
                        disabled={disabled}
                        onChange={event => handleCellChange(rowIndex || 0, column.key, event.target.value)}
                        onPaste={event => handleStructuredPaste(event, rowIndex || 0, column.key)}
                        style={getInputStyle(rowIndex || 0, column.key)}
                      />
                    </div>
                  )

                  if (!shouldShowCellTooltip(currentValue, columnWidth)) {
                    return inputNode
                  }

                  return <Tooltip title={currentValue}>{inputNode}</Tooltip>
                },
              }
            }),
          ]}
          style={{ borderTop: "1px solid #f0f0f0" }}
        />
      )}
    </Card>
  )
}

export default AssetPayloadEditor
